/* eslint-disable no-console */
import { ref } from 'vue';

const countData = ref([]);
const algoliaCounts = ref(null);
let requestId = 0;
let requestController = null;

const countBucket = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const filterCheckers = {
  type: (a, b) => {
    if (Array.isArray(b)) {
      return b.includes(a);
    }

    if (typeof b === 'string' && b.includes(',')) {
      const types = b.split(',').map((t) => t.trim());

      return types.includes(a);
    }

    return a === b;
  },

  universities: (a, b) => {
    if (Array.isArray(a)) {
      return a.includes(b);
    }

    return a === b;
  },

  area_id: (a, b) => a === b,
  price_min: (a, b) => parseInt(a) >= parseInt(b),
  price_max: (a, b) => parseInt(a) <= parseInt(b),
  bedroom_count: (a, b) => parseInt(a) === parseInt(b),
  bathroom_count: (a, b) => parseInt(a) >= parseInt(b),
};

export default () => {
  const isLoading = ref(false);

  // Get the filter count endpoint.
  const getEndpoint = (city) => `/api/v1/properties/${city}/filter-counts`;

  // Fetch the count data for the city. Algolia searches pass the current filters and
  // receive aggregated facet counts instead of the property-level dataset.
  const fetchCounts = async (city, filters = {}, { algolia = false } = {}) => {
    if (!city) {
      return;
    }

    const id = ++requestId;
    requestController?.abort();
    requestController = new AbortController();
    const controller = requestController;
    isLoading.value = true;

    try {
      const params = new URLSearchParams();

      if (algolia) {
        params.set('algolia', '1');
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          return;
        }

        params.set(key, Array.isArray(value) ? value.join(',') : String(value));
      });

      const query = params.toString();
      const res = await fetch(
        `${getEndpoint(city)}${query ? `?${query}` : ''}`,
        { signal: controller.signal }
      );

      if (id !== requestId) {
        return;
      }

      if (!res.ok) {
        console.error('Failed to get filter counts');

        return;
      }

      const payload = await res.json();

      if (id !== requestId) {
        return;
      }

      if (payload && payload.source === 'algolia') {
        algoliaCounts.value = {
          source: 'algolia',
          total: Number(payload.total) || 0,
          areas: countBucket(payload.areas),
          universities: countBucket(payload.universities),
          types: countBucket(payload.types),
        };
        countData.value = [];

        return;
      }

      algoliaCounts.value = null;
      countData.value = Array.isArray(payload) ? payload : [];
    } catch (error) {
      if (id === requestId && error.name !== 'AbortError') {
        console.error('Failed to get filter counts', error);
      }
    } finally {
      if (id === requestId) {
        isLoading.value = false;
        requestController = null;
      }
    }
  };

  const usesAlgoliaCounts = () => algoliaCounts.value?.source === 'algolia';

  const algoliaFilterCount = (group, id) => {
    const counts = algoliaCounts.value;

    if (!counts) {
      return 0;
    }

    if (group === 'total') {
      return Number(counts.total) || 0;
    }

    return Number(counts[group]?.[String(id)]) || 0;
  };

  const filterResults = (filters) => {
    const properties = Object.keys(filters);

    // Store the filtered data.
    let filteredData = countData.value;

    properties.forEach((property) => {
      const value = filters[property] ?? undefined;

      filteredData = filteredData.filter((data) => {
        const filterChecker = filterCheckers[property] ?? undefined;

        // Skip the filter check if we don't have the filter checker, property or value.
        if (!filterChecker || !data[property] || !value) {
          return true;
        }

        return filterChecker(data[property], value);
      });
    });

    return filteredData;
  };

  const filterCount = (filters) => {
    const results = filterResults(filters);

    return results.length ?? 0;
  };

  return {
    isLoading,

    algoliaCounts,
    algoliaFilterCount,
    filterCount,
    filterResults,
    fetchCounts,
    usesAlgoliaCounts,
  };
};
